// src/pages/corrales/CorralPricingAdminPage.tsx
import React, { useCallback, useEffect, useState } from "react";
import { corralPricingAdminApi, PricingTier } from "../../api/corralPricingAdminApi";
import {
  corralPurchasesAdminApi,
  CorralPurchaseAdminItem,
  CorralPurchaseStatus,
} from "../../api/corralPurchasesAdminApi";

function formatCOP(value: number | null | undefined) {
  if (value == null) return "—";
  return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(value);
}

const PURCHASE_STATUS_LABEL: Record<CorralPurchaseStatus, string> = {
  PENDING: "Pendiente de pago",
  PAID: "Pagada",
  FAILED: "Fallida",
  CANCELLED: "Cancelada",
  REFUNDED: "Reembolsada",
};

function PurchaseStatusBadge({ status }: { status: CorralPurchaseStatus }) {
  const color =
    status === "PAID"
      ? "#0cbb1d"
      : status === "PENDING"
        ? "#f98115"
        : status === "REFUNDED"
          ? "#3B82F6"
          : "#D60D4C";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: "0.72rem",
        fontWeight: 700,
        background: `${color}1F`,
        color,
      }}
    >
      {PURCHASE_STATUS_LABEL[status]}
    </span>
  );
}

export default function CorralPricingAdminPage() {
  const [basePrice, setBasePrice] = useState<number>(0);
  const [currency, setCurrency] = useState("COP");
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [editBasePrice, setEditBasePrice] = useState("");
  const [savingBase, setSavingBase] = useState(false);
  const [editingTierId, setEditingTierId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ discountPercentage?: string; fixedPrice?: string; active?: boolean }>({});
  const [savingTier, setSavingTier] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // ── Auditoría de compras ──────────────────────────────────────────────
  const [purchases, setPurchases] = useState<CorralPurchaseAdminItem[]>([]);
  const [purchasesTotal, setPurchasesTotal] = useState(0);
  const [purchasesPage, setPurchasesPage] = useState(0);
  const [purchasesStatus, setPurchasesStatus] = useState<CorralPurchaseStatus | "">("");
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const PURCHASES_PAGE_SIZE = 20;

  const showMsg = (msg: string, isError = false) => {
    if (isError) {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(""), 4000);
    } else {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(""), 4000);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await corralPricingAdminApi.obtener();
      const content = res.data?.content;
      if (content) {
        setBasePrice(content.basePrice);
        setCurrency(content.currency);
        setEditBasePrice(String(content.basePrice));
        setTiers(
          [...content.tiers].sort((a, b) => a.sortOrder - b.sortOrder),
        );
      }
    } catch {
      // vacío
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadPurchases = useCallback(async () => {
    setLoadingPurchases(true);
    try {
      const res = await corralPurchasesAdminApi.listar(purchasesPage, PURCHASES_PAGE_SIZE, purchasesStatus);
      setPurchases(res.data?.content ?? []);
      setPurchasesTotal(res.data?.totalElements ?? 0);
    } catch {
      setPurchases([]);
    } finally {
      setLoadingPurchases(false);
    }
  }, [purchasesPage, purchasesStatus]);

  useEffect(() => {
    loadPurchases();
  }, [loadPurchases]);

  const handleReconcile = async (id: string) => {
    setConfirmingId(id);
    try {
      const res = await corralPurchasesAdminApi.reconciliar(id);
      const status = (res.data?.content as any)?.status;
      if (status === "APPROVED") {
        showMsg("✓ Wompi confirmó el pago (APPROVED). Los Corrales quedaron activos para el usuario.");
      } else {
        showMsg(`Wompi reportó el estado "${status ?? "desconocido"}" — no se activó nada nuevo.`);
      }
      await loadPurchases();
    } catch (e: any) {
      showMsg(e?.response?.data?.mensaje || e?.response?.data?.message || "Error al consultar Wompi", true);
    } finally {
      setConfirmingId(null);
    }
  };

  const purchasesTotalPages = Math.max(1, Math.ceil(purchasesTotal / PURCHASES_PAGE_SIZE));

  const handleSaveBase = async () => {
    const value = Number(editBasePrice);
    if (!Number.isFinite(value) || value < 0) {
      showMsg("El precio base debe ser un número válido", true);
      return;
    }
    setSavingBase(true);
    try {
      await corralPricingAdminApi.actualizarBase(value, currency);
      showMsg(`✓ Precio base actualizado a ${formatCOP(value)} ${currency}`);
      await load();
    } catch (e: any) {
      showMsg(e?.response?.data?.mensaje || "Error al guardar el precio base", true);
    } finally {
      setSavingBase(false);
    }
  };

  const startEditTier = (tier: PricingTier) => {
    setEditingTierId(tier.id);
    setEditValues({
      discountPercentage: tier.discountPercentage != null ? String(tier.discountPercentage) : "",
      fixedPrice: tier.fixedPrice != null ? String(tier.fixedPrice) : "",
      active: tier.active,
    });
  };

  const handleSaveTier = async (tier: PricingTier) => {
    setSavingTier(true);
    try {
      await corralPricingAdminApi.actualizarTier(tier.id, {
        quantity: tier.quantity ?? undefined,
        unlimited: tier.unlimited,
        discountPercentage: tier.unlimited
          ? undefined
          : Number(editValues.discountPercentage ?? 0),
        fixedPrice: tier.unlimited ? Number(editValues.fixedPrice ?? 0) : undefined,
        active: editValues.active ?? true,
        sortOrder: tier.sortOrder,
      });
      showMsg(`✓ Tier ${tier.unlimited ? "ilimitado" : tier.quantity} actualizado`);
      setEditingTierId(null);
      await load();
    } catch (e: any) {
      showMsg(e?.response?.data?.mensaje || "Error al guardar el tier", true);
    } finally {
      setSavingTier(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">
          <i className="bi bi-cash-stack me-2" style={{ color: "var(--fg-green)" }} />
          Precios de Corral
        </h1>
        <p className="page-subtitle">
          Precio base y descuentos por volumen — cambios aquí afectan de inmediato lo que ve la app.
        </p>
      </div>

      {successMsg && (
        <div className="alert alert-success d-flex align-items-center gap-2 mb-3" style={{ borderRadius: 10 }}>
          <i className="bi bi-check-circle-fill" />
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="alert alert-danger d-flex align-items-center gap-2 mb-3" style={{ borderRadius: 10 }}>
          <i className="bi bi-exclamation-triangle-fill" />
          {errorMsg}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" style={{ color: "var(--fg-green)" }} />
        </div>
      ) : (
        <>
          {/* Precio base */}
          <div className="fg-card mb-4">
            <div className="fg-card-header">
              <div className="fg-card-title">
                <i className="bi bi-tag me-2" style={{ color: "var(--fg-green)" }} />
                Precio base por Corral
              </div>
            </div>
            <div style={{ padding: "18px 22px" }}>
              <p style={{ color: "var(--fg-muted)", fontSize: "0.85rem", marginBottom: 12 }}>
                Precio vigente actual: <strong>{formatCOP(basePrice)} {currency}</strong>. Al cambiarlo se cierra el
                registro anterior y queda histórico (auditable).
              </p>
              <div className="d-flex gap-2" style={{ maxWidth: 320 }}>
                <input
                  type="number"
                  className="form-control"
                  style={{ borderRadius: 8 }}
                  value={editBasePrice}
                  min={0}
                  onChange={(e) => setEditBasePrice(e.target.value)}
                />
                <button
                  className="btn-fg-primary"
                  onClick={handleSaveBase}
                  disabled={savingBase || Number(editBasePrice) === basePrice}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {savingBase ? <span className="spinner-border spinner-border-sm" /> : "Guardar"}
                </button>
              </div>
            </div>
          </div>

          {/* Tiers */}
          <div className="fg-card" style={{ overflow: "hidden", padding: 0 }}>
            <div className="fg-card-header">
              <div className="fg-card-title">
                <i className="bi bi-percent me-2" style={{ color: "var(--fg-green)" }} />
                Escala de descuentos
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ background: "var(--fg-bg)", fontSize: "0.78rem" }}>
                  <tr>
                    <th style={{ padding: "10px 16px" }}>Plan</th>
                    <th style={{ padding: "10px 16px" }}>Descuento</th>
                    <th style={{ padding: "10px 16px" }}>Precio fijo</th>
                    <th style={{ padding: "10px 16px" }}>Total estimado</th>
                    <th style={{ padding: "10px 16px" }}>Activo</th>
                    <th style={{ padding: "10px 16px" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((tier) => {
                    const isEditing = editingTierId === tier.id;
                    return (
                      <tr key={tier.id} style={{ fontSize: "0.875rem" }}>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle", fontWeight: 600 }}>
                          {tier.unlimited ? "Ilimitado" : `${tier.quantity} corral${tier.quantity === 1 ? "" : "es"}`}
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                          {tier.unlimited ? (
                            "—"
                          ) : isEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: 90, borderRadius: 6 }}
                              value={editValues.discountPercentage}
                              onChange={(e) => setEditValues((v) => ({ ...v, discountPercentage: e.target.value }))}
                            />
                          ) : (
                            `${tier.discountPercentage}%`
                          )}
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                          {!tier.unlimited ? (
                            "—"
                          ) : isEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: 130, borderRadius: 6 }}
                              value={editValues.fixedPrice}
                              onChange={(e) => setEditValues((v) => ({ ...v, fixedPrice: e.target.value }))}
                            />
                          ) : (
                            `${formatCOP(tier.fixedPrice)} ${currency}`
                          )}
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle", color: "var(--fg-green)", fontWeight: 700 }}>
                          {formatCOP(tier.total)} {currency}
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                          {isEditing ? (
                            <input
                              type="checkbox"
                              checked={editValues.active ?? true}
                              onChange={(e) => setEditValues((v) => ({ ...v, active: e.target.checked }))}
                            />
                          ) : (
                            <span
                              className="badge-estado"
                              style={{
                                background: tier.active ? "rgba(12,187,29,0.12)" : "rgba(179,179,179,0.2)",
                                color: tier.active ? "#0cbb1d" : "#888",
                              }}
                            >
                              {tier.active ? "Activo" : "Inactivo"}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                          {isEditing ? (
                            <div className="d-flex gap-2">
                              <button
                                className="btn-fg-primary"
                                style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                                disabled={savingTier}
                                onClick={() => handleSaveTier(tier)}
                              >
                                {savingTier ? <span className="spinner-border spinner-border-sm" /> : "Guardar"}
                              </button>
                              <button
                                className="btn-fg-outline"
                                style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                                onClick={() => setEditingTierId(null)}
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn-fg-outline"
                              style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                              onClick={() => startEditTier(tier)}
                            >
                              <i className="bi bi-pencil me-1" />
                              Editar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Compras de Corrales (auditoría) */}
          <div className="fg-card mt-4" style={{ overflow: "hidden", padding: 0 }}>
            <div className="fg-card-header">
              <div className="fg-card-title">
                <i className="bi bi-receipt me-2" style={{ color: "var(--fg-green)" }} />
                Compras de Corrales
              </div>
              <select
                className="form-select form-select-sm"
                style={{ borderRadius: 8, width: 200 }}
                value={purchasesStatus}
                onChange={(e) => {
                  setPurchasesStatus(e.target.value as CorralPurchaseStatus | "");
                  setPurchasesPage(0);
                }}
              >
                <option value="">Todos los estados</option>
                {(Object.keys(PURCHASE_STATUS_LABEL) as CorralPurchaseStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {PURCHASE_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </div>

            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ background: "var(--fg-bg)", fontSize: "0.78rem" }}>
                  <tr>
                    <th style={{ padding: "10px 16px" }}>Usuario</th>
                    <th style={{ padding: "10px 16px" }}>Plan</th>
                    <th style={{ padding: "10px 16px" }}>Total</th>
                    <th style={{ padding: "10px 16px" }}>Estado</th>
                    <th style={{ padding: "10px 16px" }}>Comprada</th>
                    <th style={{ padding: "10px 16px" }}>Vence</th>
                    <th style={{ padding: "10px 16px" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingPurchases && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: 40, color: "var(--fg-muted)" }}>
                        <div className="spinner-border spinner-border-sm text-success me-2" />
                        Cargando…
                      </td>
                    </tr>
                  )}
                  {!loadingPurchases && purchases.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: 40, color: "var(--fg-muted)" }}>
                        <i className="bi bi-inbox" style={{ fontSize: "2rem", display: "block", marginBottom: 8 }} />
                        No hay compras registradas todavía.
                      </td>
                    </tr>
                  )}
                  {!loadingPurchases &&
                    purchases.map((p) => (
                      <tr key={p.id} style={{ fontSize: "0.875rem" }}>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                          <div style={{ fontWeight: 600 }}>{p.userFullName}</div>
                          <div style={{ fontSize: "0.72rem", color: "var(--fg-muted)" }}>{p.userEmail || p.userId}</div>
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                          {p.unlimited ? "Ilimitado" : `${p.quantity} corral${p.quantity === 1 ? "" : "es"}`}
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle", fontWeight: 700, color: "var(--fg-green)" }}>
                          {formatCOP(p.total)} {p.currency}
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                          <PurchaseStatusBadge status={p.status} />
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle", fontSize: "0.8rem", color: "var(--fg-muted)" }}>
                          {p.purchaseDate ? new Date(p.purchaseDate).toLocaleString("es-CO") : "—"}
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle", fontSize: "0.8rem", color: "var(--fg-muted)" }}>
                          {p.expiresAt ? new Date(p.expiresAt).toLocaleDateString("es-CO") : "—"}
                        </td>
                        <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                          {p.status === "PENDING" && (
                            <button
                              className="btn-fg-success"
                              style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                              disabled={confirmingId === p.id}
                              onClick={() => handleReconcile(p.id)}
                              title="Vuelve a consultarle a Wompi el estado real de esta transacción — nunca aprueba directamente"
                            >
                              {confirmingId === p.id ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <>
                                  <i className="bi bi-arrow-repeat me-1" />
                                  Verificar con Wompi
                                </>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {purchasesTotalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--fg-bg)" }}>
                <button disabled={purchasesPage === 0} onClick={() => setPurchasesPage((p) => p - 1)} className="btn-fg-outline" style={{ padding: "4px 12px" }}>
                  ‹ Anterior
                </button>
                <span style={{ fontSize: "0.82rem", color: "var(--fg-muted)" }}>
                  Pág. {purchasesPage + 1} / {purchasesTotalPages}
                </span>
                <button disabled={purchasesPage >= purchasesTotalPages - 1} onClick={() => setPurchasesPage((p) => p + 1)} className="btn-fg-outline" style={{ padding: "4px 12px" }}>
                  Siguiente ›
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
